import { useState } from 'react';
import type { CategorySpecification, MachineSpecificationValue, SpecificationKey } from '../types';

type SpecificationValues = Partial<Record<SpecificationKey, string>>;

interface UseSpecificationsFormReturn {
  specifications: SpecificationValues;
  specErrors: SpecificationValues;
  setSpecificationValue: (key: SpecificationKey, value: string) => void;
  setSpecifications: (values: SpecificationValues) => void;
  clearStaleSpecifications: (allowedKeys: SpecificationKey[]) => void;
  validateSpecifications: (categorySpecs: CategorySpecification[], sortedKeys: SpecificationKey[], requiredMessage: string) => boolean;
  buildSpecificationsPayload: (sortedKeys: SpecificationKey[]) => MachineSpecificationValue[];
}

function toInitialValues(initial: MachineSpecificationValue[] | undefined): SpecificationValues {
  const values: SpecificationValues = {};
  (initial ?? []).forEach((s) => { values[s.key] = String(s.value); });
  return values;
}

// Shared numeric-input state (string values while editing, validation, payload building) for
// the per-category "Specificaties" fields — used by both MachineFormPage (create) and
// DetailActionDialog (edit) so the two forms can't drift out of sync with each other.
export function useSpecificationsForm(initial?: MachineSpecificationValue[]): UseSpecificationsFormReturn {
  const [specifications, setSpecificationsState] = useState<SpecificationValues>(() => toInitialValues(initial));
  const [specErrors, setSpecErrors] = useState<SpecificationValues>({});

  const setSpecificationValue = (key: SpecificationKey, value: string) => {
    setSpecificationsState((prev) => ({ ...prev, [key]: value }));
    setSpecErrors((e) => ({ ...e, [key]: undefined }));
  };

  // Wholesale replace — e.g. prefilling from a selected machine template. Also clears stale
  // errors, since a full replacement makes any previous validation state irrelevant.
  const setSpecifications = (values: SpecificationValues) => {
    setSpecificationsState(values);
    setSpecErrors({});
  };

  // Called whenever the selected category changes — drops any value for a key the new category
  // no longer configures, and clears stale validation errors from the previous category.
  const clearStaleSpecifications = (allowedKeys: SpecificationKey[]) => {
    setSpecificationsState((prev) => {
      const next: SpecificationValues = {};
      allowedKeys.forEach((k) => { if (prev[k] !== undefined) next[k] = prev[k]; });
      return next;
    });
    setSpecErrors({});
  };

  const validateSpecifications = (
    categorySpecs: CategorySpecification[],
    sortedKeys: SpecificationKey[],
    requiredMessage: string
  ): boolean => {
    const errs: SpecificationValues = {};
    sortedKeys.forEach((k) => {
      const spec = categorySpecs.find((s) => s.key === k);
      const raw = specifications[k];
      if (spec?.required && (raw === undefined || raw.trim() === '')) {
        errs[k] = requiredMessage;
      }
    });
    setSpecErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const buildSpecificationsPayload = (sortedKeys: SpecificationKey[]): MachineSpecificationValue[] =>
    sortedKeys
      .filter((k) => specifications[k] !== undefined && specifications[k]!.trim() !== '')
      .map((k) => ({ key: k, value: Number(specifications[k]) }));

  return {
    specifications,
    specErrors,
    setSpecificationValue,
    setSpecifications,
    clearStaleSpecifications,
    validateSpecifications,
    buildSpecificationsPayload,
  };
}
